import { Badge, Box, Button, Divider, Grid, IconButton, Popover, Typography, useTheme } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';

import React, { useState, MouseEvent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import WSTextField from '../TextBox/WSTextBox';
import WSSelectField from '../Select/WSSelect';
import WSDateField from '../DateField/WSDateField';
import WSMultiCheckBox from '../CheckBox/WSMultiCheckBox';
import { IOptions } from '../Lib/Lib';
import WSRadioField from '../Radio/WSRadio';
import { convertToISO, formatDate, IsSmallScreen } from '../../commonFunctions/CommonFunctions';

interface IFilterProps {
    rows: IRows[];
    onFilterChange: (filters: any) => void;
    alignment?: 'left' | 'center' | 'right';
    headerType?: "simple" | "HaderTopCornered" | "modern",
    dateType?: "date" | "datetime";
}

export interface IRows {
    type: "text" | "checkbox" | "date" | "dropdown" | "radio";
    label: string;
    fieldName: string;
    options?: IOptions[];
    range?: boolean;
    operator?: string;
    align?: 'horizontal' | 'vertical';
    rowDivision?: 6 | 12;
}

interface IFilter {
    field: string;
    operator: Operator | string;
    value: string | number;
}

export enum Operator {
    EqualTo = 'eq',
    NotEqualTo = 'neq',
    GreaterThan = 'gt',
    LessThan = 'lt',
    Like = 'like',
}

export const WSFilterComponent: React.FC<IFilterProps> = React.memo(({ rows, onFilterChange, alignment = 'left', headerType = "simple", dateType = "date" }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [activeFiltersCount, setActiveFiltersCount] = useState(0);
    const methods = useForm();
    const { register, handleSubmit, control, reset, formState: { isDirty } } = methods;
    const isMobile = IsSmallScreen();
    const theme = useTheme();

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        reset();
    };

    const handleReset = () => {
        const emptyValues = rows.reduce((acc, row) => {
            acc[row.fieldName] = ''; // or [] for multi-select/checkbox fields
            return acc;
        }, {} as Record<string, any>);

        reset(emptyValues);
        onFilterChange([]);
        setActiveFiltersCount(0);
        reset();
    };

    const open = Boolean(anchorEl);
    const id = open ? 'filter-popover' : undefined;

    const removeEmptyFilters = (filters: any) => {
        return Object.keys(filters).reduce((acc, key) => {
            const value = filters[key];
            if (value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)) {
                acc[key] = value;
            }
            return acc;
        }, {} as any);
    };

    const onSubmit = (data: any) => {
        console.log("Form Data for filter on Submit:", data);
        const cleanedFilters = removeEmptyFilters(data);
        const activeCount = Object.keys(cleanedFilters).length;
        setActiveFiltersCount(activeCount);

        console.log("Cleaned Filters (non-empty):", cleanedFilters);

        const appliedFilters = rows.reduce((acc, column) => {
            console.log(`Checking column: ${column.fieldName} with type: ${column.type}`);
            if (cleanedFilters[column.fieldName]) {
                console.log(`Processing filter for field: ${column.fieldName}, value: ${cleanedFilters[column.fieldName]}, operator: ${column.operator || Operator.EqualTo}`);
                let value = cleanedFilters[column.fieldName];

                if (column.type === "date") {
                    if (dateType === "datetime") {
                        // For start of day
                        value = convertToISO(cleanedFilters[column.fieldName], false);

                        // Or if it's an end-date in a range
                        // value = convertToISO(cleanedFilters[column.fieldName], true);
                    } else {
                        value = formatDate(cleanedFilters[column.fieldName]); // for plain date
                    }
                }

                console.log(`Adding filter - field: ${column.fieldName}, operator: ${column.operator || Operator.EqualTo}, value: ${value}`);
                acc.push({
                    field: column.fieldName,
                    operator: column.operator || Operator.EqualTo,
                    value: value,
                });
            }
            return acc;
        }, [] as IFilter[]);

        console.log('Applied Filters:', appliedFilters);
        onFilterChange(appliedFilters);
        setAnchorEl(null);
    };

    const sxHeaderTopCornered: any = {
        backgroundColor: theme.palette.primary.main,
        borderRadius: '12px',
        padding: '8px 20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
    };

    const sxSimple: any = {
        height: 32,
        width: isMobile ? 20 : undefined,
        borderRadius: '8px',
        transition: 'all 0.2s ease',
    };

    return (
        <>
            <Box display="flex" justifyContent={alignment} sx={{ height: isMobile ? 33 : 32, marginTop: isMobile ? -2.2 : undefined }}>
                <Badge
                    badgeContent={activeFiltersCount}
                    color="error"
                    sx={{
                        '& .MuiBadge-badge': {
                            fontSize: '10px',
                            height: '18px',
                            minWidth: '18px',
                            borderRadius: '9px',
                        }
                    }}
                >
                    <Button
                        sx={headerType === 'simple' ? sxSimple : sxHeaderTopCornered}
                        aria-describedby={id}
                        variant="contained"
                        onClick={handleClick}
                        style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            letterSpacing: '0.3px',
                        }}
                        startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
                    >
                        Filter
                    </Button>
                </Badge>
            </Box>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                style={{ maxWidth: 1000, marginTop: 8 }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        width: isMobile ? '95vw' : 720,   // 🔥 CONTROL WIDTH HERE
                        maxWidth: '95vw',
                        borderRadius: '16px',
                        boxShadow: '0 20px 35px -8px rgba(0,0,0,0.2)',
                        overflow: 'hidden',
                    }
                }}
            >
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)} className="popover-content">
                        {/* Header with gradient */}
                        <Box
                            sx={{
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                padding: '16px 20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box display="flex" alignItems="center" gap={1}>
                                <FilterListIcon sx={{ color: 'white', fontSize: 20 }} />
                                <Typography style={{ fontWeight: 600, color: 'white', fontSize: '1rem', letterSpacing: '0.3px' }}>
                                    Filter Options
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={handleClose}
                                sx={{
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                    },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>

                        <Divider />

                        {/* Filter Fields */}
                        <Box sx={{ padding: '20px', paddingTop: "56px", maxHeight: isMobile ? '70vh' : '65vh', overflowY: 'auto' }}>
                            <Grid container spacing={2.5}>
                                {rows.map(({ type, fieldName, label, options, align }) => (
                                    <Grid item xs={12} sm={6} md={4} key={fieldName}>
                                        <Box sx={{ mb: 2 }}>
                                            {type === 'text' && (
                                                <WSTextField
                                                    control={control}
                                                    id={fieldName}
                                                    {...register(fieldName)}
                                                    name={fieldName}
                                                    label={label}
                                                    size="small"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '10px',
                                                            '&:hover fieldset': {
                                                                borderColor: theme.palette.primary.main,
                                                            },
                                                        },
                                                    }}
                                                />
                                            )}
                                            {type === 'dropdown' && (
                                                <WSSelectField
                                                    options={options}
                                                    {...register(fieldName)}
                                                    id={fieldName}
                                                    control={control}
                                                    isMobile={isMobile}
                                                    label={label}
                                                    name={fieldName}

                                                />
                                            )}
                                            {type === 'date' && (
                                                <WSDateField
                                                    id={fieldName}
                                                    name={fieldName}
                                                    label={label}
                                                    dateFormat="DD-MM-YYYY"
                                                    control={control}
                                                    defaultValue={null}
                                                    rules={{ required: false }}

                                                />
                                            )}
                                            {type === 'checkbox' && (
                                                <WSMultiCheckBox
                                                    id={fieldName}
                                                    label={label}
                                                    name={fieldName}
                                                    options={options || []}
                                                    align={align}
                                                    defaultValue={[]}
                                                    rules={{ required: false }}
                                                />
                                            )}
                                            {type === 'radio' && (
                                                <WSRadioField
                                                    id={fieldName}
                                                    label={label}
                                                    name={fieldName}
                                                    align='Horizontal'
                                                    options={options || []}
                                                    defaultValue={[]}
                                                    rules={{ required: false }}
                                                    control={control}
                                                />
                                            )}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        <Divider />

                        {/* Action Buttons */}
                        <Box sx={{ padding: '16px 20px', backgroundColor: '#fafbfc' }}>
                            <Grid container justifyContent="flex-end" spacing={2}>
                                <Grid item>
                                    <Button
                                        type="button"
                                        onClick={handleReset}
                                        variant="outlined"
                                        startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                                        sx={{
                                            borderRadius: '10px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            padding: '6px 16px',
                                            borderColor: '#d1d5db',
                                            color: '#6b7280',
                                            '&:hover': {
                                                borderColor: theme.palette.primary.main,
                                                backgroundColor: 'rgba(125, 121, 194, 0.04)',
                                            },
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                                        sx={{
                                            borderRadius: '10px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            padding: '6px 20px',
                                            backgroundColor: theme.palette.primary.main,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.dark,
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        Apply Filters
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </form>
                </FormProvider>
            </Popover>
        </>
    );
});
